import { Show } from '@refinedev/antd';
import { useCan, useInvalidate, useShow } from '@refinedev/core';
import { Descriptions, Tag, Table, Button, Popconfirm, App } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BidiText, MonoCode, formatDateBidi } from '@lebinatikra/rtl-utils';
import { filterApiFetch, FilterApiError } from '../../lib/filter-api-client';

interface UserRow {
  id: string;
  email: string | null;
  phone: string | null;
  default_locale: string;
  created_at: string;
  display_name: string | null;
  roles?: Array<{
    id: string;
    role: string;
    organization_id: string;
    joined_at: string;
    revoked_at: string | null;
  }>;
}

type RoleRow = NonNullable<UserRow['roles']>[number];

export function UserShow() {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const invalidate = useInvalidate();
  const { query } = useShow<UserRow>();
  const record = query.data?.data;
  const canEdit = useCan({ resource: 'users', action: 'edit' });
  const [revoking, setRevoking] = useState<string | null>(null);

  const handleRevoke = async (organizationId: string) => {
    if (!record) return;
    setRevoking(organizationId);
    try {
      await filterApiFetch(
        `/v1/organizations/${organizationId}/users/${record.id}/revoke`,
        { method: 'POST' },
      );
      notification.success({ message: t('notifications.access_revoked') });
      await invalidate({ resource: 'users', invalidates: ['list', 'detail'], id: record.id });
      await query.refetch();
    } catch (err) {
      if (err instanceof FilterApiError) {
        notification.error({ message: err.message });
      } else {
        notification.error({ message: t('errors.generic') });
      }
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Show isLoading={query.isLoading}>
      {record ? (
        <>
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('common.id')}>
              {/* rtl-utils:bidi-wrapped */}
              <MonoCode>{record.id}</MonoCode>
            </Descriptions.Item>
            <Descriptions.Item label={t('auth.email')}>
              {record.email ? (
                // rtl-utils:bidi-wrapped
                <BidiText dir="ltr">{record.email}</BidiText>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.phone')}>
              {record.phone ? (
                // rtl-utils:bidi-wrapped
                <BidiText dir="ltr">{record.phone}</BidiText>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.default_locale')}>
              <Tag>{record.default_locale}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {formatDateBidi(record.created_at)}
            </Descriptions.Item>
          </Descriptions>
          <h3 style={{ marginTop: 24 }}>{t('fields.roles')}</h3>
          <Table
            rowKey={(r: RoleRow) => r.id}
            dataSource={record.roles ?? []}
            pagination={false}
            columns={[
              {
                dataIndex: 'organization_id',
                title: t('fields.organization_id'),
                render: (v: string) => (
                  // rtl-utils:bidi-wrapped
                  <MonoCode>{v}</MonoCode>
                ),
              },
              {
                dataIndex: 'role',
                title: t('fields.role'),
                render: (v: string, row: RoleRow) => (
                  <Tag color={row.revoked_at ? 'red' : 'blue'}>{v}</Tag>
                ),
              },
              {
                dataIndex: 'joined_at',
                title: t('common.created_at'),
                render: (v: string) => formatDateBidi(v),
              },
              {
                dataIndex: 'revoked_at',
                title: t('fields.revoked_at'),
                render: (v: string | null) =>
                  v ? (
                    <Tag color="red">{formatDateBidi(v)}</Tag>
                  ) : (
                    <span style={{ opacity: 0.5 }}>—</span>
                  ),
              },
              {
                title: t('common.actions'),
                render: (_: unknown, row: RoleRow) =>
                  canEdit.data?.can && row.revoked_at === null ? (
                    <Popconfirm
                      title={t('confirm.revoke_access_warning')}
                      okText={t('actions.revoke_access')}
                      okButtonProps={{ danger: true }}
                      cancelText={t('common.cancel')}
                      onConfirm={() => handleRevoke(row.organization_id)}
                    >
                      <Button danger size="small" loading={revoking === row.organization_id}>
                        {t('actions.revoke_access')}
                      </Button>
                    </Popconfirm>
                  ) : null,
              },
            ]}
          />
        </>
      ) : null}
    </Show>
  );
}
